import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject, filter } from 'rxjs';

/**
 * Builds a PrimeNG `MenuItem[]` breadcrumb trail from the activated route tree.
 *
 * Each route segment that carries `data.breadcrumb` becomes one crumb.  The
 * crumb's `routerLink` is the accumulated URL up to and including that segment,
 * so every crumb is individually clickable.
 *
 * Usage in a layout component:
 * ```ts
 * breadcrumbSvc = inject(BreadcrumbService);
 * breadcrumbs$ = this.breadcrumbSvc.breadcrumbs$;
 * ```
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly _crumbs$ = new BehaviorSubject<MenuItem[]>([]);

  /** Observable trail of crumbs for the current route. */
  readonly breadcrumbs$ = this._crumbs$.asObservable();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
    // Only update after navigation is fully settled — the snapshot tree is
    // not populated during the auth/callback redirect, so we must NOT seed
    // eagerly from the constructor.
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this._crumbs$.next(this.build(this.route.root)));
  }

  /**
   * Recursively walks the primary outlet route tree, collecting segments whose
   * snapshot carries a non-empty `data.breadcrumb` string.
   */
  private build(route: ActivatedRoute, url = '', crumbs: MenuItem[] = []): MenuItem[] {
    for (const child of route.children) {
      if (child.outlet !== PRIMARY_OUTLET) continue;

      // Guard: snapshot may be absent during early navigation phases.
      if (!child.snapshot) continue;

      // Accumulate the URL path for this segment.
      const segments = child.snapshot.url.map((s) => s.path);
      if (segments.length) {
        url += '/' + segments.join('/');
      }

      const label: string | undefined = child.snapshot.data['breadcrumb'];
      if (label) {
        crumbs.push({ label, routerLink: url });
      }

      // Recurse into children — we always follow the primary outlet.
      return this.build(child, url, crumbs);
    }

    return crumbs;
  }
}
